import { Component } from '@angular/core';
import { Router } from '@angular/router';
import {
    AerodromeDetailsDto,
    AuthorisationRuleOutcome,
    DisplayableMissionDto,
    LookupObject,
    RuleCodeDto,
    RuleOutcome,
    toLookup
} from '@flyfreely-portal-ui/flyfreely';
import { Angulartics2 } from 'angulartics2';
import { Subject, combineLatest, filter, takeUntil } from 'rxjs';
import {
    AirspaceCheckService,
    AirspaceCheckStatus,
    incompleteOrInvalidAirspaceCheckerStatuses
} from '..';
import { AirspaceDialoguesService } from '../../airspace-dialogues.service';
import { AirspaceAuthorisationManager } from '../../authorisation/airspace-authorisation-manager.service';

interface OutcomeDetails {
    code: string;
    name: string;
    message: string;
    relatedMissionIdList?: number[];
}

function convertToOutcomeDetails(
    outcome: RuleOutcome | AuthorisationRuleOutcome,
    messages: { [key: string]: RuleCodeDto }
) {
    const message = messages[outcome.code];
    return {
        code: outcome.code,
        name: message?.name ?? (<AuthorisationRuleOutcome>outcome)?.title,
        message: message
            ? outcome.outcome === RuleOutcome.Outcome.BLOCK
                ? message.blockMessage
                : message.adviseMessage
            : (<AuthorisationRuleOutcome>outcome).message,
        relatedMissionIdList: (<AuthorisationRuleOutcome>outcome)
            .relatedMissionIdList
    };
}

@Component({
    selector: 'inline-airspace-warnings-and-blockers',
    templateUrl: './inline-warnings-and-blockers.component.html',
    styles: [
        `
            .warnings-container {
                overflow-x: auto;
                overflow-y: clip;
            }

            .outcome-card {
                padding: 5px 15px;
                border-radius: 5px;
                box-shadow: 0 2px 2px #00000033;
                font-weight: 500;
                margin-right: 10px;
                min-width: fit-content;
            }

            .outcome-card.pad {
                margin-right: 15px;
            }

            .checking-container {
                padding: 5px 15px;
                font-weight: 500;
            }

            .airspace-authorisation-result {
                display: flex;
                height: 100%;
                padding-left: 5%;
                align-items: center;
            }

            .airspace-authorisation-result.pass {
                position: relative;
                background: #fff;
                color: #30d455;
            }

            .alert-triangle {
                padding: 5px;
                margin-right: 10px;

                span {
                    font-size: 2em;
                }
            }
        `
    ],
    styleUrls: ['../../styles.scss']
})
export class InlineAirspaceWarningsAndBlockersComponent {
    airspaceCheckAdvises: OutcomeDetails[] = [];
    airspaceCheckBlocks: OutcomeDetails[] = [];
    authorisationPreviewAdvises: OutcomeDetails[] = [];
    authorisationPreviewBlocks: OutcomeDetails[] = [];

    aerodromes: { [code: string]: AerodromeDetailsDto };
    authorisationRuleOutcomes: AuthorisationRuleOutcome[];
    airspaceRuleOutcomes: RuleOutcome[];
    messages: { [key: string]: RuleCodeDto };

    airspaceStatus: AirspaceCheckStatus;
    airspaceCheckNotRun = true;
    canShowAirspaceDetails = false;

    warningIcon = '<span class="fa fa-exclamation-triangle"></span>';
    jurisdictionAnnouncements: string[];

    relatedMissionLookup: LookupObject<DisplayableMissionDto>;

    timeZone?: string;

    private ngUnsubscribe$ = new Subject<void>();
    constructor(
        private airspaceCheckService: AirspaceCheckService,
        private airspaceDialoguesService: AirspaceDialoguesService,
        private airspaceAuthorisationManager: AirspaceAuthorisationManager,
        private angulartics2: Angulartics2,
        private router: Router
    ) {}

    ngOnInit() {
        combineLatest([
            this.airspaceCheckService.resultStatus$,
            this.airspaceAuthorisationManager.checkingAuthorisation$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([status, checkingAuthorisation]) => {
                this.airspaceStatus = checkingAuthorisation
                    ? 'CHECKING'
                    : status;
                this.airspaceCheckNotRun =
                    incompleteOrInvalidAirspaceCheckerStatuses.includes(status);
            });

        combineLatest([
            this.airspaceCheckService.result$,
            this.airspaceAuthorisationManager.airspaceAuthorisationPreview$,
            this.airspaceCheckService.airspaceJurisdiction$
        ])
            .pipe(
                filter(
                    ([result, preview, jurisdiction]) =>
                        result != null || preview != null
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(([result, preview, jurisdiction]) => {
                this.canShowAirspaceDetails =
                    jurisdiction?.airspaceCheckSupport === 'AIRSPACE_CHECK';

                this.jurisdictionAnnouncements = result?.announcementList;
                this.timeZone = result?.timeZone;
                this.aerodromes = result?.aerodromes;
                this.authorisationRuleOutcomes =
                    preview?.authorisationRuleOutcomeList;
                this.airspaceRuleOutcomes = result?.ruleOutcomes;
                this.messages = result?.ruleMessages;
                this.airspaceCheckAdvises = (result?.ruleOutcomes ?? [])
                    .filter(o => o.outcome === RuleOutcome.Outcome.ADVISE)
                    .map(outcome =>
                        convertToOutcomeDetails(outcome, result?.ruleMessages)
                    )
                    .reduce(
                        (acc: OutcomeDetails[], outcome: OutcomeDetails) =>
                            acc.find(
                                o =>
                                    o.code === outcome.code &&
                                    o.name === outcome.name
                            ) != null
                                ? acc
                                : acc.concat(outcome),
                        []
                    );
                this.authorisationPreviewAdvises = (
                    preview?.authorisationRuleOutcomeList ?? []
                )
                    .filter(o => o.outcome === RuleOutcome.Outcome.ADVISE)
                    .map(outcome => convertToOutcomeDetails(outcome, {}))
                    .reduce(
                        (acc: OutcomeDetails[], outcome: OutcomeDetails) =>
                            acc.find(
                                o =>
                                    o.code === outcome.code &&
                                    o.name === outcome.name
                            ) != null
                                ? acc
                                : acc.concat(outcome),
                        []
                    );
                this.airspaceCheckBlocks = (result?.ruleOutcomes ?? [])
                    .filter(o => o.outcome === RuleOutcome.Outcome.BLOCK)
                    .map(outcome =>
                        convertToOutcomeDetails(outcome, result?.ruleMessages)
                    )
                    .reduce(
                        (acc: OutcomeDetails[], outcome: OutcomeDetails) =>
                            acc.find(
                                o =>
                                    o.code === outcome.code &&
                                    o.name === outcome.name
                            ) != null
                                ? acc
                                : acc.concat(outcome),
                        []
                    );
                this.authorisationPreviewBlocks = (
                    preview?.authorisationRuleOutcomeList ?? []
                )
                    .filter(o => o.outcome === RuleOutcome.Outcome.BLOCK)
                    .map(outcome => convertToOutcomeDetails(outcome, {}))
                    .reduce(
                        (acc: OutcomeDetails[], outcome: OutcomeDetails) =>
                            acc.find(
                                o =>
                                    o.code === outcome.code &&
                                    o.name === outcome.name
                            ) != null
                                ? acc
                                : acc.concat(outcome),
                        []
                    );
            });

        this.airspaceAuthorisationManager.relatedMissions$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(relatedMissions => {
                this.relatedMissionLookup = relatedMissions.reduce(
                    toLookup,
                    {}
                );
            });
    }

    showAuthorisationOutcomeDetails(adviseOrBlock: OutcomeDetails) {
        const outcome = this.authorisationRuleOutcomes.find(
            o => o.code === adviseOrBlock.code
        );
        this.airspaceDialoguesService.showAuthorisationOutcomeDetailsDialogue(
            outcome
        );

        this.angulartics2.eventTrack.next({
            action: 'view-authorisation-outcome-details',
            properties: {
                category: 'airspace-authorisation',
                label: 'inline-warnings-and-blockers'
            }
        });
    }

    showAirspaceRuleDetails(airspaceOutcome: OutcomeDetails) {
        const outcome = this.airspaceRuleOutcomes.find(
            o => o.code === airspaceOutcome.code
        );
        const message = this.messages[outcome.code];

        this.airspaceDialoguesService.showRuleDetailsDialogue(
            this.aerodromes,
            [outcome],
            message,
            this.timeZone
        );

        this.angulartics2.eventTrack.next({
            action: 'view-rule-details',
            properties: {
                category: 'airspace-check',
                label: 'inline-warnings-and-blockers'
            }
        });
    }

    showAirspaceDetails() {
        this.airspaceDialoguesService.showAirspaceCheckDetailsDialogue(
            this.airspaceCheckService
        );
    }

    goToRelatedMission(id: number) {
        this.airspaceAuthorisationManager.goToRelatedMission(id);
    }
}
